import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis';
import { AnalysisFeedbackListDrawerHeader } from '@modules/common/components/AnalysisFeedbackListDrawerHeader';
import { AnalysisFeedbackListDrawerSecondaryNode } from '@modules/common/components/AnalysisFeedbackListDrawerSecondaryNode';
import { DEFAULT_DATE_TIME_RANGE } from '@modules/common/components/DateRangePicker/constants';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefact } from '@modules/common/components/SaveArtefact';
import { NAV_PATHS } from '@modules/common/constants';
import { useCopyToClipboard, useGetArtefactScope } from '@modules/common/hooks';
import { ArtefactType, SaveArtefactFunction } from '@modules/common/types';
import { isInternalUser } from '@modules/common/utils';
import { getSearchMetadataUrl } from '@modules/feed/utils';
import { getSearchLink } from '@modules/feed/utils/getSearchLink';
import { getSearchRoute } from '@modules/feed/utils/getSearchRoute';
import { AddToNewCollectionDialog } from '@modules/feedback/components/AddToNewCollectionDialog';
import { OpenInCollectionLink } from '@modules/feedback/components/FeedbackCard/OpenInCollectionLink';
import { FeedbackListDrawer } from '@modules/feedback/components/FeedbackListDrawer';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useUpdateSavedFeedbackStoreWithMetadata } from '@modules/oracle/hooks';
import { useSetAnalysisFromCitation } from '@modules/oracle/hooks/useSetAnalysisFromCitation';
import { trackWisdomEvents } from '@modules/oracle/utils';
import {
  getCollectionMetadata,
  trackCollectionEvents,
} from '@modules/savedCollection/utils';
import { SavedFeedbackListActionType } from '@modules/savedFeedbackList/store/savedFeedbackListActionType';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { Button, Link } from '@mui/material';
import { AnalysisUseCase, AnalysisViewFragment } from 'generated/graphql';
import { SyntheticEvent, useEffect, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import { Scope } from 'v2/core/entities/scope';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { ParsedEQLProps, parseEQL } from 'v2/core/repository/eql/parser';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import pathJoin from 'v2/core/utils/path';

interface ArtefactCitationFeedbackListProps {
  shouldShowFeedbackDrawer: boolean;
  closeFeedbackDrawer: () => void;
  analysis: AnalysisViewFragment | undefined;
  queryContainer: QueryContainer;
  defaultPrompt?: string;
  quoteContextClaim?: string;
}

export const ArtefactCitationFeedbackList = ({
  shouldShowFeedbackDrawer,
  closeFeedbackDrawer,
  analysis,
  queryContainer,
  defaultPrompt,
  quoteContextClaim,
}: ArtefactCitationFeedbackListProps) => {
  const {
    id: userID,
    tenant: { timezone },
  } = useGetUser();

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer?.WhereClause,
  });
  const dispatch = useSavedFeedbackListStore(({ dispatch }) => dispatch);

  useUpdateSavedFeedbackStoreWithMetadata({ analysis });

  const [isAddToNewCollectionDialogOpen, setIsAddToNewCollectionDialogOpen] =
    useState(false);
  const [nameOfCollection, setNameOfCollection] = useState(
    analysis?.title ?? '',
  );
  const [descriptionOfCollection, setDescriptionOfCollection] = useState(
    analysis?.description ?? '',
  );
  const {
    tenant: { slug: tenantSlug, id: tenantID },
  } = useGetUser();

  const { showSuccessNotification } = useNotificationContext();

  const { openModal } = useMuiModalContext();

  const artefactScope = useGetArtefactScope();

  const analysisUseCase = analysis?.useCase;

  useSetAnalysisFromCitation({ analysis: analysis });
  const copyToClipboard = useCopyToClipboard();

  const onCopy = () => {
    if (!analysis) {
      return;
    }
    if (analysisUseCase === AnalysisUseCase.Feed) {
      const feedLink = getSearchLink({
        name: analysis.title,
        token: analysis.viewToken,
        isSearchToFeedEnabled: true,
        tenantSlug,
      });
      copyToClipboard(feedLink);
      trackWisdomEvents({
        eventName: 'Wisdom - Copied Artefact Link from Feedback Citation View',
        artefact: {
          id: analysis.id,
          title: analysis.title,
          useCase: analysis.useCase,
          link: feedLink,
        },
      });
    } else if (analysisUseCase === AnalysisUseCase.Collection) {
      const collectionLink = pathJoin(
        window.location.origin,
        tenantSlug ?? '',
        `/${NAV_PATHS.COLLECTION}/${analysis.viewToken}`,
      );
      copyToClipboard(collectionLink);
      trackWisdomEvents({
        eventName: 'Wisdom - Copied Artefact Link from Feedback Citation View',
        artefact: {
          id: analysis.id,
          title: analysis.title,
          useCase: analysis.useCase,
          link: collectionLink,
        },
      });
    }
  };

  let eqlQuery: ParsedEQLProps = {
    expressionType: ExpressionType.Records,
  };

  if (sourceDetailsMap) {
    eqlQuery = analysis
      ? parseEQL(analysis.context.eqlExpression, sourceDetailsMap, timezone)
      : eqlQuery;
  }

  const whereClause = eqlQuery.whereClause ?? EmptyWhereClause;

  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause,
    dateRange: eqlQuery.dateRange ?? (DEFAULT_DATE_TIME_RANGE as TimeRange),
  };
  const { createAnalysis, isLoading: isCreatingAnalysis } = useCreateAnalysis({
    useCase: analysisUseCase ?? AnalysisUseCase.Default,
    name: '',
    scope: Scope.HIDDEN,
    eqlProps,
  });

  const onSave: SaveArtefactFunction = async ({ name, scope }) => {
    let feedMetadata = '';
    let collectionMetadata;

    if (!analysis) {
      return {
        artefactLink: '',
        artefactRoute: '',
      };
    }

    if (analysisUseCase === AnalysisUseCase.Feed) {
      feedMetadata = getSearchMetadataUrl({
        tenantSlug,
        duration:
          (eqlProps.dateRange as DateTimeRange) ??
          (DEFAULT_DATE_TIME_RANGE as DateTimeRange),
        whereClause: eqlProps.whereClause ?? EmptyWhereClause,
        isSearchToFeedEnabled: true,
      });
    }

    if (analysisUseCase === AnalysisUseCase.Collection) {
      collectionMetadata = getCollectionMetadata(
        analysis.metadataV2?.collection?.savedFeedbackMetadata,
      );
    }

    const resp = await createAnalysis({
      variables: {
        title: name,
        scope: mapToScope(scope) ?? artefactScope,
        tenantID,
        panels: [],
        metadata: feedMetadata,
        metadataV2: collectionMetadata
          ? {
              collection: {
                savedFeedbackMetadata: collectionMetadata,
              },
            }
          : undefined,
        createdBy: userID,
        analysisExpresssion: analysis.context.eqlExpression,
        description: descriptionOfCollection || analysis.description,
      },
    });

    if (typeof resp === 'function' || !resp.data?.createAnalysis?.id) {
      return {
        artefactLink: '',
        artefactRoute: '',
      };
    }

    const savedAnalysisData = resp.data.createAnalysis;

    const artefactRoute = getSearchRoute({
      name,
      token: savedAnalysisData.viewToken,
      isSearchToFeedEnabled: true,
    });

    const artefactLink = getSearchLink({
      name,
      token: savedAnalysisData.viewToken,
      tenantSlug,
      isSearchToFeedEnabled: true,
    });

    if (analysisUseCase === AnalysisUseCase.Collection) {
      showSuccessNotification({
        contentComponent: `"${name}" created successfully`,
        props: {
          severity: 'success',
          timeout: 15000,
          isClosable: true,
          action: (
            <OpenInCollectionLink
              viewToken={resp.data?.createAnalysis?.viewToken}
            />
          ),
        },
      });
    }

    if (analysisUseCase === AnalysisUseCase.Feed) {
      showSuccessNotification({
        contentComponent: `"${name}" created successfully`,
        props: {
          severity: 'success',
          timeout: 15000,
          isClosable: true,
          action: (
            <Button LinkComponent={Link} href={artefactLink}>
              Open
            </Button>
          ),
        },
      });
    }

    return {
      artefactLink,
      artefactRoute,
    };
  };

  const onAddToNewCollectionDialogClose = (_: SyntheticEvent) => {
    setIsAddToNewCollectionDialogOpen(false);
  };

  const onCreateNewCollection = async (
    event?: SyntheticEvent | KeyboardEvent,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();

    const scopeOfCollection = isInternalUser() ? Scope.PRIVATE : Scope.PUBLIC;

    await onSave({ name: nameOfCollection, scope: scopeOfCollection });

    event && onAddToNewCollectionDialogClose(event as SyntheticEvent);

    trackCollectionEvents({
      eventName: 'Create New Collection',
      name: nameOfCollection,
      description: descriptionOfCollection,
    });
  };

  const onCreateNewArtefact = () => {
    if (!analysis) {
      return;
    }

    if (analysisUseCase === AnalysisUseCase.Feed) {
      return openModal({
        contentComponent: () => (
          <SaveArtefact
            onSubmit={onSave}
            artefactType={ArtefactType.Feed}
            defaultValue={analysis.title}
            shouldShowArtefactDefinitionInfo={false}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      });
    }

    if (analysisUseCase === AnalysisUseCase.Collection) {
      setIsAddToNewCollectionDialogOpen(true);
    }

    trackWisdomEvents({
      eventName:
        'Wisdom - Clicked on Save As New Artefact from Feedback Citation View',
      artefact: {
        id: analysis.id,
        title: analysis.title,
        useCase: analysis.useCase,
      },
    });
  };

  const handleCloseDrawer = () => {
    closeFeedbackDrawer();
    dispatch({
      type: SavedFeedbackListActionType.RESET,
    });
  };

  useEffect(() => {
    if (analysis?.title) {
      setNameOfCollection(analysis?.title);
    }
    if (analysis?.description) {
      setDescriptionOfCollection(analysis?.description);
    }
  }, [analysis?.title, analysis?.description]);

  return (
    <>
      <FeedbackListDrawer
        open={shouldShowFeedbackDrawer}
        onClose={handleCloseDrawer}
        queryContainer={queryContainer}
        selectedFeedbackMetadata={selectedFeedbackMetadata}
        shouldHideOpenInSearch
        defaultPrompt={defaultPrompt}
        quoteContextClaim={quoteContextClaim}
        feedbackListViewType={
          analysisUseCase === AnalysisUseCase.Collection
            ? FeedbackListViewType.SAVED
            : FeedbackListViewType.DEFAULT
        }
        shouldShowLoader={!analysis}
        header={{
          shouldEnableSort: true,
          renderTitle: () => {
            if (!analysis) {
              return null;
            }
            return (
              <AnalysisFeedbackListDrawerHeader
                useCase={analysisUseCase ?? AnalysisUseCase.Default}
                title={analysis.title}
                createdBy={`${analysis.createdBy.firstName} ${analysis.createdBy.lastName}`}
              />
            );
          },
          secondaryActionNode: !!analysis && (
            <AnalysisFeedbackListDrawerSecondaryNode
              onCopy={onCopy}
              useCase={analysisUseCase ?? AnalysisUseCase.Default}
              onCreateNewArtefact={onCreateNewArtefact}
            />
          ),
          renderDescription: () => {
            return null;
          },
        }}
        shouldSkipSummary={analysisUseCase === AnalysisUseCase.Collection}
      />
      {isAddToNewCollectionDialogOpen && (
        <AddToNewCollectionDialog
          onClose={onAddToNewCollectionDialogClose}
          onCreateNewCollection={onCreateNewCollection}
          isAddingInProgress={isCreatingAnalysis}
          nameOfCollection={nameOfCollection}
          setNameOfCollection={setNameOfCollection}
          descriptionOfCollection={descriptionOfCollection}
          setDescriptionOfCollection={setDescriptionOfCollection}
        />
      )}
    </>
  );
};
